<script lang="ts" setup>
import axiosApiClient from '@/axios';
import { ref, onMounted, inject } from 'vue';
import { useLocaleStore } from '@/stores/locale';
import { QuillEditor } from '@vueup/vue-quill';
import { useI18n } from 'vue-i18n';
import { IconFilePlus } from '@tabler/icons-vue';


const swal = inject('$swal') as any;
const t = useI18n().t;
const localeStore = useLocaleStore();
const openAddEditModal = ref(false)
const isbusy = ref(false);
// const checked = ref(false);

const configurations = ref();
const fetchGeneralConfigs = async () => {
    try {
        const response = await axiosApiClient.get('/api/configurations/general');
        configurations.value = response.data;
    } catch (error) {
        console.error('Error fetching configurations:', error);
    }
};


interface Project {
    id: number;
    name: string;
    description: string;
    address: string;
    status: string,
    cost: number,
    duration: number,
    duration_unit: string,
    ingredients: string,
    end_date?: string,
    start_date?: string,
    owner?: string,
    type?: string,
    category?: string,
    project_manager?: string,
    space?: number,
    buildings?: number,
    units?: number,
    sold_units?: number,
    available_units?: number,
}
interface Media {
    id: number;
    file_name: string;
    file_content: string;
    file_type: string;
    file_size: number;
    file_extension: string;
}
interface Unit {
    id: number;
    name: string;
    description: string;
    project_id: number;
    type: string;
    status: string;
    cost: number;
    space: number;
    thumbnail: string | null;
}
const fetchOurProjects = async () => {
    isbusy.value = true;
    const { data } = await axiosApiClient.get('/api/projects');
    return data;
}
const projects = ref<Project[]>([]);
const projectData = ref<Project>({
    id: 0,
    name: '',
    description: '',
    address: '',
    status: 'completed',
    cost: 0,
    duration: 0,
    duration_unit: 'months',
    ingredients: '',
    end_date: '',
    start_date: '',
    owner: '',
    type: 'project',
    category: 'residential',
    project_manager: '',
    space: 0,
    buildings: 0,
    units: 0,
    sold_units: 0,
    available_units: 0,
});
const units = ref<Unit[]>();
const unitData = ref<Unit>({
    id: 0,
    name: 'New Unit',
    description: 'New Unit Description',
    project_id: 0,
    type: 'unit',
    status: 'completed',
    cost: 0,
    space: 0,
    thumbnail: null,
});
const ingredient_editor = ref('');
onMounted(async () => {
    await fetchGeneralConfigs();
    projects.value = await fetchOurProjects();
    isbusy.value = false;

    projectData.value.owner = configurations.value.find((config: { key: string; value: string }) => config.key === 'corp_name')?.value || import.meta.env.VITE_APP_NAME;
});

const editProject = (id: number) => {
    //open modal and set project data to be edited
    const project = projects.value.find((project) => project.id === id);
    if (project) {
        projectData.value = { ...project };
        ingredient_editor.value = project.ingredients;
        fetchProjectMedia();
        fetchProjectUnits();
        openAddEditModal.value = true;
    }
    else {
        console.error('Project not found')
    }
}
const deleteProject = async (id: number) => {
    //swal confirm
    const result = await swal.fire({
        title: t('Are you sure?'),
        text: t('You won\'t be able to revert this!'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: t('Yes, delete it!')
    });
    if (result.isConfirmed) {
        await axiosApiClient.delete(`/api/projects/${id}`)
        projects.value = await fetchOurProjects();
        swal.fire({
            icon: 'success',
            title: t('Deleted!'),
            text: t('Your project has been deleted.'),
            position: 'top-end',
            toast: true,
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
        });
    }
    else {
        swal.fire({
            icon: 'error',
            title: t('Cancelled'),
            text: t('Your project is safe :)'),
            position: 'top-end',
            toast: true,
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
        });
    }
    isbusy.value = false;
}
const addProject = () => {
    projectData.value = {
        id: 0,
        name: '',
        description: '',
        address: '',
        status: 'completed',
        cost: 0,
        duration: 0,
        duration_unit: 'months',
        ingredients: '',
        end_date: '',
        start_date: '',
        owner: import.meta.env.VITE_APP_NAME,
        type: 'project',
        category: 'residential',
        project_manager: '',
        space: 0,
        buildings: 0,
        units: 0,
        sold_units: 0,
        available_units: 0,
    };
    ingredient_editor.value = '';
    openAddEditModal.value = true
}

const upsert = async () => {
    isbusy.value = true;
    // Add your logic to save the project here
    // For example, you can use axios to send a POST request to your API
    projectData.value.ingredients = ingredient_editor.value;
    if (projectData.value.id > 0) {
        await axiosApiClient.put(`/api/projects/${projectData.value.id}`, projectData.value)
    } else {
        await axiosApiClient.post('/api/projects', projectData.value)
    }
    openAddEditModal.value = false
    projects.value = await fetchOurProjects();
    // Reset the form
    projectData.value = {
        id: 0,
        name: '',
        description: '',
        address: '',
        status: 'completed',
        cost: 0,
        duration: 0,
        duration_unit: 'months',
        ingredients: '',
        end_date: '',
        start_date: '',
        owner: import.meta.env.VITE_APP_NAME,
        type: 'project',
        category: 'residential',
        project_manager: '',
        space: 0,
        buildings: 0,
        units: 0,
        sold_units: 0,
        available_units: 0,

    };
    ingredient_editor.value = '';
    // Show success message
    swal.fire({
        icon: 'success',
        title: t('Project saved successfully'),
        position: 'top-end',
        toast: true,
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
    }).then(() => {
        // Fetch the updated projects list
        fetchOurProjects();
        isbusy.value = false;
    });

}
const uploadAttachments = async (event: any) => {
    if (projectData.value.id == 0 || projectData.value.id == undefined)
        return swal.fire({
            icon: 'error',
            title: t('Please save the project first'),
            position: 'top-end',
            toast: true,
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
        }).then(() => {
            isbusy.value = false;
        });
    if (event.target.files.length == 0) return swal.fire({
        icon: 'error',
        title: t('Please select a file to upload'),
        position: 'top-end',
        toast: true,
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
    }).then(() => {
        isbusy.value = false;
    });

    //check file size
    if (event.target.files[0].size > 1024 * 1024 * 1) {
        return swal.fire({
            icon: 'error',
            title: t('File size should be less than 1MB'),
            showConfirmButton: false,
            timer: 19500,
            timerProgressBar: true,
            footer: `
            <div style="width: 100%;text-align: center; font-size: 14px; color: #000;">
            ".webp" ${t('Format Images is Preffered')}
            <br/>
            ${t('Suggestion')}
            <br/>
            ${t('Convert your images format')}
            <br/>
            <a href="https://image.online-convert.com/convert-to-webp">${t('Images Converter')}</a>
            </div>`,
        }).then(() => {
            isbusy.value = false;
        });
    }
    isbusy.value = true
    const files = event.target.files;
    const formData = new FormData();
    formData.append("entity_id", projectData.value.id.toString());
    formData.append("entity_name", "projects");
    //save files[0] as base64 string in the database
    const reader = new FileReader();
    reader.readAsDataURL(files[0]);
    reader.onload = async () => {
        const base64String = reader.result as string;
        formData.append("file_content", base64String.split(',')[1]);
        //append other data to formData
        formData.append("file_name", files[0].name);
        formData.append("file_type", files[0].type);
        formData.append("file_size", files[0].size);
        formData.append("file_extension", files[0].name.split('.').pop());
        try {
            await axiosApiClient.post("/api/documents", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            await fetchProjectMedia();
        } catch (error) {
            console.error(error);
        }
        isbusy.value = false;
    };
};
const projectMedias = ref<Media[]>([]);
const fetchProjectMedia = async () => {
    if (projectData.value.id == 0 || projectData.value.id == undefined) {
        projectMedias.value = []
        return;
    }
    const { data } = await axiosApiClient.get(`/api/documents/projects/${projectData.value.id}`)
    projectMedias.value = data
}
const enlargImage = (id: number) => {
    const media = projectMedias.value.find((media) => media.id === id)
    if (media) {
        openImageModal(media.file_name, `data:image/png;base64,${media.file_content}`);
    }
}
const openImageModal = (name: string, src: string) => {
    const modal = document.createElement('div');
    modal.className = 'img-modal';
    modal.innerHTML = `
        <div class="modal-content z-[999999999]">
            <span class="close text-2xl cursor-pointer">&times;</span>
            <img src="${src}" alt="${name}" style="width: 100%; height: auto;" />
        </div>
    `;
    document.body.appendChild(modal);
    const closeBtn = modal.querySelector('.close') as HTMLElement;
    if (closeBtn) {
        closeBtn.onclick = () => {
            document.body.removeChild(modal);
        };
    }
    // Close modal when clicking outside of the image
    modal.onclick = (event) => {
        if (event.target === modal) {
            document.body.removeChild(modal);
        }
    };
};

const fetchProjectUnits = async () => {
    if (projectData.value.id == 0 || projectData.value.id == undefined) {
        units.value = []
        return;
    }
    const { data } = await axiosApiClient.get(`/api/projects/${projectData.value.id}/units`)
    units.value = data
}
const addUnit = async () => {
    if (projectData.value.id == 0 || projectData.value.id == undefined) {
        return swal.fire({
            icon: 'error',
            title: t('Please save the project first'),
            position: 'top-end',
            toast: true,
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
        });
    }
    unitData.value.project_id = projectData.value.id;
    await axiosApiClient.post(`/api/projects/${projectData.value.id}/units`, unitData.value);
    unitData.value = {
        id: 0,
        name: 'New Unit',
        description: 'New Unit Description',
        project_id: 0,
        type: 'unit',
        status: 'completed',
        cost: 0,
        space: 0,
        thumbnail: null,
    };
    await fetchProjectUnits();

}
const deleteMedia = async (id: number) => {
    const result = await swal.fire({
        title: t('Are you sure?'),
        text: t('You won\'t be able to revert this!'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: t('Yes, delete it!'),
        cancelButtonText: t('Cancel'),
    });
    if (result.isConfirmed) {
        await axiosApiClient.delete(`/api/documents/${id}`)

        swal.fire({
            icon: 'success',
            title: t('Deleted!'),
            text: t('Your media has been deleted.'),
            position: 'top-end',
            toast: true,
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
        }).then(async () => {
            await fetchProjectMedia();
        });
    }
    else {
        swal.fire({
            icon: 'error',
            title: t('Cancelled'),
            text: t('Your media is safe :)'),
            position: 'top-end',
            toast: true,
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
        });
    }
}
const editUnit = (id: number) => {
    const unit = units.value?.find((unit) => unit.id === id);
    if (unit) {
        unitData.value = { ...unit };
        //openUnitModal.value = true;
    }
    else {
        console.error('Unit not found')
    }
}
const deleteUnit = async (id: number) => {
    const result = await swal.fire({
        title: t('Are you sure?'),
        text: t('You won\'t be able to revert this!'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: t('Yes, delete it!'),
        cancelButtonText: t('Cancel'),
    });
    if (result.isConfirmed) {
        await axiosApiClient.delete(`/api/projects/${projectData.value.id}/units/${id}`)
        swal.fire({
            icon: 'success',
            title: t('Deleted!'),
            text: t('Your unit has been deleted.'),
            position: 'top-end',
            toast: true,
            showConfirmButton: false,
        })
    }
}

</script>

<template>
    <admin-layout>
        <loading-spinner :is-loading="isbusy" />
        <!--view projects as a table with edit/ delete options-->
        <div class="flex-col items-center justify-center mt-8">
            <div class="w-full flex justify-between mt-4">
                <h1 class="text-3xl font-bold text-center mt-8">{{ t('Our Projects') }}</h1>
                <button @click="addProject" class="flex items-center h-25 p-2 bg-blue-500 text-white rounded">{{ t('Add')
                    }}
                    <IconFilePlus stroke={2} />
                </button>
            </div>
            <table class="table table-striped w-full mt-8 text-center">
                <thead>
                    <tr>
                        <th class="px-4 py-2">{{ t('ID') }}</th>
                        <th class="px-4 py-2">{{ t('Name') }}</th>
                        <th class="px-4 py-2">{{ t('Description') }}</th>
                        <th class="px-4 py-2">{{ t('Status') }}</th>
                        <th class="px-4 py-2"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="project in projects" :key="project.id">
                        <td class="border px-4 py-2">{{ project.id }}</td>
                        <td class="border px-4 py-2">{{ project.name }}</td>
                        <td class="border px-4 py-2">{{ project.description }}</td>
                        <td class="border px-4 py-2">{{ t(project.status) }}</td>
                        <td class="border px-4 py-2 flex gap-4 justify-center items-center">
                            <!-- Add edit and delete buttons here -->
                            <a href="#!" @click.prevent="editProject(project.id)">
                                <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true"
                                    xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                    viewBox="0 0 24 24">
                                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                                        stroke-width="2"
                                        d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z" />
                                </svg>
                            </a>
                            <a href="#!" @click.prevent="deleteProject(project.id)" class="text-red-400">
                                <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24"
                                    height="24" fill="none" viewBox="0 0 24 24">
                                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z" />
                                </svg>
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Add / Edit Modal -->
        <div v-if="openAddEditModal" class="custom-modal overflow-auto" id="projectAddEditModal"
            @click.self="openAddEditModal = false">
            <div class="modal-content absolute h-[100%] overflow-auto"
                :dir="localeStore.locale == 'ar' ? 'rtl' : 'ltr'">
                <div class="modal-header">
                    <h4 class="font-bold mb-4">{{ t('Add / Edit Project') }}</h4>
                </div>
                <div class="tabs w-full h-full mt-0">
                    <!-- Tab Buttons -->
                    <input type="radio" name="tabs" id="tab1" checked>
                    <label for="tab1">{{ t('Basic Info.') }}</label>

                    <input type="radio" name="tabs" id="tab2">
                    <label for="tab2">{{ t('Project Units') }}</label>

                    <input type="radio" name="tabs" id="tab3">
                    <label for="tab3">{{ t('Project Media') }}</label>

                    <!-- Tab Contents -->
                    <div class="tab-content mb-8" id="basic_info">
                        <form @submit.prevent="upsert">
                            <div class="flex gap-2 flex-wrap">
                                <div class="mb-4 flex flex-col flex-grow">
                                    <label for="project_name" class="block text-gray-700">{{ t('Project Name')
                                        }}</label>
                                    <input type="text" id="name" v-model="projectData.name"
                                        class="border rounded w-full py-2 px-3" required />
                                </div>
                                <div class="flex-grow">
                                    <label for="project_owner" class="block text-gray-700">{{ t('Owner') }}</label>
                                    <input type="text" id="project_owner" v-model="projectData.owner"
                                        class="border rounded w-full py-2 px-3" required />
                                </div>
                                <div>
                                    <label for="project_type" class="block text-gray-700">{{ t('Type') }}</label>
                                    <input type="text" id="project_type" v-model="projectData.type"
                                        class="border rounded w-full py-2 px-3" />
                                </div>
                                <div>
                                    <label for="project_category" class="block text-gray-700">{{ t('Category')
                                        }}</label>
                                    <input type="text" id="project_category" v-model="projectData.category"
                                        class="border rounded w-full py-2 px-3" />
                                </div>
                            </div>
                            <div class="mb-4">
                                <label for="description" class="block text-gray-700">{{ t('Description') }}</label>
                                <textarea id="description" v-model="projectData.description"
                                    class="border rounded w-full py-2 px-3"></textarea>
                            </div>
                            <div class="mb-4">
                                <label for="ingredients" class="block text-gray-700">{{ t('Ingredients') }}</label>
                                <!--ingredients-->
                                <div class="text-gray-700 mb-2">
                                    <QuillEditor v-model:content="ingredient_editor" content-type="html" toolbar="full" />
                                </div>
                            </div>
                            <div class="mb-4">
                                <label for="address" class="block text-gray-700">{{ t('Address') }}</label>
                                <input type="text" id="address" v-model="projectData.address"
                                    class="border rounded w-full py-2 px-3" />
                            </div>
                            <div class="flex gap-2">
                                <div class="mb-4 flex-grow w-25">
                                    <label for="status" class="block text-gray-700">{{ t('Status') }}</label>
                                    <select id="status" v-model="projectData.status"
                                        class="border rounded w-full py-2 px-3">
                                        <option value="completed">{{ t('Completed') }}</option>
                                        <option value="inprogress">{{ t('InProgress') }}</option>
                                        <option value="pending">{{ t('Pending') }}</option>
                                    </select>
                                </div>
                                <div class="mb-4">
                                    <label for="cost" class="block text-gray-700">{{ t('Cost') }}</label>
                                    <input type="number" id="cost" v-model="projectData.cost"
                                        class="border rounded w-full py-2 px-3" />
                                </div>
                                <div class="flex flex-wrap gap-2 justify-start">
                                    <div class="flex-grow">
                                        <label for="duration" class="block text-gray-700">{{ t('Duration') }}</label>
                                        <input type="number" id="duration" v-model="projectData.duration"
                                            class="border rounded w-full py-2 px-3" />
                                    </div>
                                    <div>
                                        <select id="duration_unit" v-model="projectData.duration_unit"
                                            class="border rounded w-full py-2 px-3 mt-4">
                                            <option value="days">{{ t('Days') }}</option>
                                            <option value="weeks">{{ t('Weeks') }}</option>
                                            <option value="months">{{ t('Months') }}</option>
                                            <option value="years">{{ t('Years') }}</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label for="end_date" class="block text-gray-700">{{ t('End Date') }}</label>
                                        <input type="text" id="end_date" v-model="projectData.end_date"
                                            class="border rounded w-full py-2 px-3" placeholder="YYYY-MM-DD" />
                                    </div>
                                    <!--thumbnail-->
                                    <!-- <div>
                                        <label for="thumbnail" class="block text-gray-700">{{ t('Thumbnail') }}</label>
                                        <input type="file" id="thumbnail" @change="uploadAttachments"
                                            class="border rounded w-full py-2 px-3" />
                                    </div> -->
                                </div>
                            </div>
                            <div class="w-full flex justify-between">
                                <!-- <button type="submit"
                                    class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">{{
                                    t('Save') }}</button> -->
                                <button type="submit" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                                    :disabled="isbusy">
                                    <span v-if="isbusy">
                                        <loading-spinner :is-loading="true" />
                                    </span>
                                    <span v-else>{{ t('Save') }}</span>
                                </button>
                                <button @click="openAddEditModal = false"
                                    class="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600">{{ t('Close')
                                    }}</button>
                            </div>
                        </form>
                    </div>

                    <div class="tab-content" id="units_info">
                        <!--add remove units-->
                        <div class="flex gap-3 flex-wrap">
                            <!--add new unit-->
                            <div class="flex flex-col mt-4">
                                <label for="unit_name" class="block text-gray-700">{{ t('Unit Name') }}</label>
                                <input type="text" id="unit_name" v-model="unitData.name"
                                    class="border rounded w-full py-2 px-3" required />
                            </div>
                            <div class="flex flex-col mt-4">
                                <label for="unit_cost" class="block text-gray-700">{{ t('Unit Price') }}</label>
                                <input type="number" id="unit_cost" v-model="unitData.cost"
                                    class="border rounded w-full py-2 px-3" />
                            </div>
                            <div class="flex flex-col mt-4">
                                <label for="unit_space" class="block text-gray-700">{{ t('Unit Space') }}</label>
                                <input type="number" id="unit_space" v-model="unitData.space"
                                    class="border rounded w-full py-2 px-3" />
                            </div>
                        </div>
                        <div class="flex flex-col mt-4 flex-grow">
                            <label for="unit_description" class="block text-gray-700">{{ t('Unit Description')
                                }}</label>
                            <textarea id="unit_description" v-model="unitData.description"
                                class="border rounded w-full py-2 px-3"></textarea>
                        </div>
                        <div class="mt-4 flex justify-center">
                            <button @click="addUnit"
                                class="bg-green-300 text-white px-4 py-2 rounded hover:bg-green-400">{{
                                    t('Add Unit') }}</button>
                        </div>
                        <div>
                            <!--units list-->
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>{{ t('Unit Name') }}</th>
                                        <th>{{ t('Unit Price') }}</th>
                                        <th>{{ t('Unit Space') }}</th>
                                        <th>{{ t('Unit Description') }}</th>
                                        <th>{{ t('Actions') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(unit, index) in units" :key="index">
                                        <td>{{ unit.name }}</td>
                                        <td>{{ unit.cost }}</td>
                                        <td>{{ unit.space }}</td>
                                        <td>{{ unit.description }}</td>
                                        <td>
                                            <button @click="editUnit(unit.id)"
                                                class="bg-blue-300 text-white px-2 py-1 rounded hover:bg-blue-400">{{
                                                    t('Edit') }}</button>
                                            <button @click="deleteUnit(unit.id)"
                                                class="bg-red-300 text-white px-2 py-1 rounded hover:bg-red-400">{{
                                                    t('Delete') }}</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="tab-content" id="media_files">
                        <!--media files-->
                        <div class="mb-4">
                            <label for="media" class="block text-gray-700">{{ t('Media') }}</label>
                            <input type="file" id="media" class="border rounded w-full py-2 px-3"
                                @change="uploadAttachments" />
                        </div>
                        <div class="flex flex-col mt-1 w-full">
                            <!--preview area-->
                            <h4 class="text-gray-700">{{ t('Preview') }}</h4>
                            <div class="flex flex-wrap gap-2 mt-2">
                                <div v-for="media in projectMedias" :key="media.id"
                                    class="border rounded p-2 flex flex-col items-center flex-grow">
                                    <img :src="'data:image/png;base64,' + media.file_content" alt="Media"
                                        class="w-full h-auto mb-2" @click="enlargImage(media.id)" />
                                    <p>{{ media.file_name }}</p>
                                    <div class="flex gap-1">
                                        <button @click.prevent="deleteMedia(media.id)"
                                            class="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600">{{
                                                t('Delete')
                                            }}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </admin-layout>
</template>


<style lang="scss" scoped>
.custom-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
}

.custom-modal .modal-content {
    background-color: #fff;
    padding: 20px;
    border-radius: 5px;
    width: 80%;
    max-width: 500px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.custom-modal .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.custom-modal .modal-header h2 {
    margin: 0;
}

.editor-wrapper {
    max-width: 600px;
    margin: 0 auto;
    padding: 1rem;
}

.modal-content {
    max-width: 800px !important;
    margin: 0 auto;
    padding: 20px;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}


.tabs {
    max-width: 800px;
    margin: 20px auto;
}

/* Hide radio buttons */
.tabs input[type="radio"] {
    display: none;
}

/* Style labels as tabs */
.tabs>label {
    display: inline-block;
    padding: 10px 20px;
    cursor: pointer;
    background: #f0f0f0;
    border: 1px solid #ccc;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    margin-right: 5px;
    position: relative;
    top: 1px;
}

/* Active tab label */
.tabs input[type="radio"]:checked+label {
    background: #fff;
    border-bottom: 1px solid #fff;
}

/* Tab content container */
.tab-content {
    display: none;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 0 4px 4px 4px;
}

/* Show content when radio is checked */
#tab1:checked~#basic_info,
#tab2:checked~#units_info,
#tab3:checked~#media_files {
    display: block;
}
</style>